import { ReactNode, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/*
 * Stepper / StepCard — DESIGN_SYSTEM.md §2.4
 * Vertical accordion of booking steps. Each step has number badge, title,
 * summary line, optional Edit button. Completing a step locks-edit, expands
 * the next.
 *
 * Usage:
 *   <Stepper>
 *     <StepCard step={1} title="Service" state="done" summary="Haircut · 30m"
 *               onEdit={…} />
 *     <StepCard step={2} title="Time" state="active">
 *       …body…
 *     </StepCard>
 *     <StepCard step={3} title="Confirm" state="locked" />
 *   </Stepper>
 */

export type StepState = 'locked' | 'active' | 'done' | 'collapsed';

const StepperContext = createContext<{ active: number | null }>({ active: null });

interface StepperProps {
  /** Reactive currently-active step number (1-indexed). When null, no card is highlighted. */
  active?: number | null;
  className?: string;
  children: ReactNode;
}

export function Stepper({ active = null, className, children }: StepperProps) {
  return (
    <StepperContext.Provider value={{ active }}>
      <div className={cn('flex flex-col gap-3', className)}>{children}</div>
    </StepperContext.Provider>
  );
}

interface StepCardProps {
  step: number;
  title: ReactNode;
  state: StepState;
  /** Summary line shown when state === 'done' or 'collapsed'. */
  summary?: ReactNode;
  onEdit?: () => void;
  /** When state === 'active', body is rendered. */
  children?: ReactNode;
  className?: string;
}

export function StepCard({ step, title, state, summary, onEdit, children, className }: StepCardProps) {
  const { t } = useTranslation('common');
  const isLocked = state === 'locked';
  const isActive = state === 'active';
  const isDone = state === 'done';
  const isCollapsed = state === 'collapsed';

  return (
    <section
      className={cn(
        'rounded-3xl border bg-surface transition-colors',
        isLocked && 'border-line opacity-60 pointer-events-none',
        isActive && 'border-brand-300 shadow-md',
        isDone && 'border-line',
        isCollapsed && 'border-line',
        className,
      )}
      aria-current={isActive ? 'step' : undefined}
    >
      <header className="flex items-center gap-3 px-5 py-4">
        <span
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-pill text-body-sm font-semibold tabular-nums',
            isDone && 'bg-success text-white',
            isActive && 'bg-brand-500 text-white',
            (isLocked || isCollapsed) && 'bg-surface-2 text-soft',
          )}
        >
          {isDone ? <Check className="h-4 w-4" strokeWidth={3} /> : step}
        </span>

        <div className="min-w-0 flex-1">
          <div className={cn('text-body font-semibold', isLocked ? 'text-text-muted' : 'text-ink')}>
            {title}
          </div>
          {summary && !isActive && (
            <div className={cn('mt-0.5 truncate text-body-sm', isDone ? 'text-ink' : 'text-soft')}>
              {summary}
            </div>
          )}
        </div>

        {onEdit && isDone && (
          <Button variant="ghost" size="sm" onClick={onEdit} iconLeft={<Pencil className="h-3.5 w-3.5" />}>
            {t('actions.edit')}
          </Button>
        )}
      </header>

      {isActive && children && (
        <div className="border-t border-line px-5 py-5">{children}</div>
      )}
    </section>
  );
}

/** Hook for child components that need to know the active step. */
export function useStepperActive(): number | null {
  return useContext(StepperContext).active;
}
